import { normalizeForCompare } from '../textNormalization';

/* ─── Sport mappings ──────────────────────────────────────────────────────── */

const SPORT_DISPLAY = {
    mlb:      'Baseball',
    nfl:      'Football',
    nba:      'Basketball',
    nhl:      'Hockey',
    soccer:   'Soccer',
    golf:     'Golf',
    tennis:   'Tennis',
    boxing:   'Boxing',
    mma:      'MMA',
    racing:   'Racing',
    wrestling: 'Wrestling',
    olympics: 'Olympics',
};

const SPORT_TO_LEAGUE = {
    mlb: 'MLB',
    nfl: 'NFL',
    nba: 'NBA',
    nhl: 'NHL',
};

export function getSportDisplay(sport) {
    if (!sport) return '';
    return SPORT_DISPLAY[sport.toLowerCase()] ?? sport.toUpperCase();
}

export function getLeague(sport) {
    if (!sport) return '';
    return SPORT_TO_LEAGUE[sport.toLowerCase()] ?? '';
}

export function isAutograph(cardType) {
    if (!cardType) return false;
    return /auto/i.test(cardType);
}

/**
 * eBay C:Card Name must be a single word. Use the checklist card type.
 * Autograph Relic is sent as Autograph.
 */
const EBAY_CARD_NAME_BY_TYPE = {
    'autograph relic': 'Autograph',
};

export function getEbayCardName(cardType) {
    if (cardType == null) return '';
    const trimmed = String(cardType).trim();
    if (!trimmed) return '';
    return EBAY_CARD_NAME_BY_TYPE[trimmed.toLowerCase()] ?? trimmed;
}

/* ─── Category IDs ────────────────────────────────────────────────────────── */

export const EBAY_CATEGORIES = [
    { label: 'Baseball Cards',    id: '261328' },
    { label: 'Football Cards',    id: '261329' },
    { label: 'Basketball Cards',  id: '261330' },
    { label: 'Hockey Cards',      id: '261331' },
    { label: 'Soccer Cards',      id: '261334' },
    { label: 'Golf Cards',        id: '261336' },
    { label: 'Multi-Sport Cards', id: '261338' },
];

/** Sports Mem → Sports Trading Cards → Trading Card Singles */
export const EBAY_SINGLES_CATEGORY_ID = '261328';

/** Sports Mem → Sports Trading Cards → Trading Card Lots */
export const EBAY_LOTS_CATEGORY_ID = '261329';

export const DEFAULT_CATEGORY_ID = EBAY_SINGLES_CATEGORY_ID;

/* ─── Condition IDs ───────────────────────────────────────────────────────── */

export const EBAY_CONDITIONS = [
    { label: 'Near mint or better: Comparable to a fresh pack',    id: '2750' },
    { label: 'Excellent: Has clearly visible signs of wear',        id: '3000' },
    { label: 'Very good: Has moderate-to-heavy damage all over',    id: '4000' },
    { label: 'Poor: Is extremely worn and displays flaws all over', id: '6000' },
];

export const DEFAULT_CONDITION_ID = '2750';


/* ─── Shared helpers ──────────────────────────────────────────────────────── */

export function toCamelCaseWords(value) {
    if (!value || typeof value !== 'string') return value;
    return value
        .toLowerCase()
        .replace(/(^|[\s-]+)([a-z])/g, (match, sep, char) => `${sep}${char.toUpperCase()}`);
}

export function stripTeamAttribution(value) {
    if (!value || typeof value !== 'string') return value;
    return value
        .replace(/[\u00A9\u00AE\u2122]/g, '')
        .replace(/\s*\((?:tm|r|c)\)\s*/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function shouldUsePrintNumber(printNumber, numberOnCard) {
    if (printNumber === null || printNumber === undefined || `${printNumber}`.trim() === '') return false;
    if (numberOnCard === null || numberOnCard === undefined || `${numberOnCard}`.trim() === '') return true;
    return `${printNumber}`.trim() !== `${numberOnCard}`.trim();
}

function buildParallelInfo(parallel, cardNumber) {
    if (!parallel) return '';
    const baseName = parallel.cardParallelName && normalizeForCompare(parallel.cardParallelName) !== 'BASE'
        ? toCamelCaseWords(parallel.cardParallelName)
        : '';
    let suffix = '';
    if (parallel.cardSupply) {
        suffix = shouldUsePrintNumber(parallel.cardPrintNumber, cardNumber)
            ? `#${parallel.cardPrintNumber}/${parallel.cardSupply}`
            : `/${parallel.cardSupply}`;
    }
    if (baseName && suffix) return `${baseName} ${suffix}`;
    if (baseName) return baseName;
    if (suffix) return suffix;
    return '';
}

/* ─── Title ───────────────────────────────────────────────────────────────── */

/**
 * Builds an eBay-optimised title.
 * @param {object} card         - card object ({ name, cardNumber, collectedCards, team })
 * @param {string} series       - series string (e.g. "2024 Topps Chrome")
 * @param {object} [parallel]   - selected parallel variation (optional)
 */
export function buildEbayTitle(card, series, parallel = null) {
    const { name, cardNumber, collectedCards, team } = card ?? {};
    const titleParts = [];

    if (series) titleParts.push(series);
    if (cardNumber) titleParts.push(`#${cardNumber}`);
    if (name?.length > 0) titleParts.push(name.join(' & '));

    // Parallel info — use provided parallel, or fall back to sole collected card
    const resolvedParallel = parallel ?? (collectedCards?.length === 1 ? collectedCards[0] : null);
    let parallelStr = '';
    if (resolvedParallel) {
        const pName = resolvedParallel.cardParallelName ?? resolvedParallel.name;
        if (pName && normalizeForCompare(pName) !== 'BASE') {
            parallelStr = toCamelCaseWords(pName);
            if (resolvedParallel.cardSupply) parallelStr += ` /${resolvedParallel.cardSupply}`;
        }
    }
    if (parallelStr) titleParts.push(parallelStr);

    // Team at the end
    if (Array.isArray(team) && team.length > 0) {
        titleParts.push(team.map(stripTeamAttribution).join(' / '));
    } else if (team && typeof team === 'string' && team.trim()) {
        titleParts.push(stripTeamAttribution(team));
    }

    const title = titleParts.join(' ');
    return title.length > 80 ? title.slice(0, 80) : title;
}

/* ─── Description ─────────────────────────────────────────────────────────── */

function resolveParallel(card, parallel = null) {
    if (parallel) return parallel;
    if (card?.collectedCards?.length === 1) return card.collectedCards[0];
    // Staged queue cards store parallel fields flat on the item
    if (card?.cardParallelName || card?.cardSupply != null || card?.cardPrintNumber != null) {
        return {
            cardParallelName: card.cardParallelName,
            cardPrintNumber:  card.cardPrintNumber,
            cardSupply:       card.cardSupply,
        };
    }
    return null;
}

function appendCardDetailLines(lines, bullet, card, series, parallel = null) {
    const names = Array.isArray(card?.name) ? card.name : (card?.name ? [card.name] : []);
    const playerName = names.length > 0 ? names.join(' & ') : '';
    const cardNumber = card?.cardNumber;
    const parallelInfoStr = buildParallelInfo(resolveParallel(card, parallel), cardNumber);

    const firstLineParts = [];
    if (cardNumber) firstLineParts.push(`#${cardNumber}`);
    if (playerName) {
        firstLineParts.push(parallelInfoStr ? `${playerName} (${parallelInfoStr})` : playerName);
    }
    if (firstLineParts.length > 0) bullet(firstLineParts.join(' - '));

    const seriesLine = series ?? card?.series;
    if (seriesLine) bullet(seriesLine);
}

function appendEbayDescriptionFooter(lines, bullet, br) {
    lines.push('🔍 Condition');
    bullet('Near Mint or Better. Card was pulled from pack, sleeved immediately, and stored in a top loader.');
    bullet('Please review photos carefully before purchasing.');
    br();

    lines.push('🚚 Shipping');
    bullet('Ships securely in a penny sleeve, top loader, and team bag.');
    bullet('eBay Standard Envelope (for eligible cards) or BMWT for higher value items.');
    bullet('Combined shipping available — add items to cart before checkout.');
    br();

    lines.push('💬 Message me with any questions — happy to help.');
}

/**
 * Builds a rich HTML eBay description for a single card.
 * @param {object} card         - card object ({ name, cardNumber, collectedCards })
 * @param {string} series       - series string
 * @param {object} [parallel]   - selected parallel variation (optional)
 */
export function buildEbayDescription(card, series, parallel = null) {
    const lines = [];
    const bullet = (value) => { if (value) lines.push(`- ${value}`); };
    const br = () => lines.push('<br>');

    lines.push('You are purchasing the exact card pictured.');
    br();

    lines.push('🏷️ Card Details');
    appendCardDetailLines(lines, bullet, card, series, parallel);
    br();

    appendEbayDescriptionFooter(lines, bullet, br);

    return lines.join('<br>');
}

/**
 * Same eBay description template as singles, with one Card Details block per lot card.
 */
export function buildEbayLotDescription(cards = []) {
    const lines = [];
    const bullet = (value) => { if (value) lines.push(`- ${value}`); };
    const br = () => lines.push('<br>');

    lines.push('You are purchasing the exact cards pictured.');
    br();

    lines.push('🏷️ Card Details');
    cards.forEach((card, index) => {
        if (index > 0) br();
        appendCardDetailLines(lines, bullet, card, card.series);
    });
    br();

    appendEbayDescriptionFooter(lines, bullet, br);

    return lines.join('<br>');
}

/* ─── Lot item specifics ──────────────────────────────────────────────────── */

function pushUnique(list, value) {
    if (value == null || value === '') return;
    const str = String(value).trim();
    if (!str || list.includes(str)) return;
    list.push(str);
}

/**
 * Aggregates eBay Item Specifics from a lot's embedded cards.
 * Multi-value fields use `|` (File Exchange convention).
 * Players keep every distinct name; teams are unique only.
 */
export function buildLotItemSpecifics(cards = []) {
    const players = [];
    const teams = [];
    const sports = [];
    const manufacturers = [];
    const parallels = [];
    const sets = [];
    const leagues = [];
    const cardNames = [];
    const cardNumbers = [];
    const printRuns = [];
    let hasAutograph = false;

    for (const card of cards) {
        const names = Array.isArray(card.name) ? card.name : (card.name ? [card.name] : []);
        names.forEach((name) => pushUnique(players, name));

        const cardTeams = Array.isArray(card.team) ? card.team : (card.team ? [card.team] : []);
        cardTeams.forEach((team) => pushUnique(teams, stripTeamAttribution(team)));

        pushUnique(sports, getSportDisplay(card.sport));
        pushUnique(manufacturers, card.manufacturer);
        pushUnique(sets, card.series);
        pushUnique(leagues, getLeague(card.sport));
        pushUnique(cardNumbers, card.cardNumber);
        pushUnique(printRuns, card.cardSupply);

        if (card.cardParallelName && card.cardParallelName.toUpperCase() !== 'BASE') {
            pushUnique(parallels, card.cardParallelName);
        }

        pushUnique(cardNames, getEbayCardName(card.cardType));

        if (isAutograph(card.cardType)) hasAutograph = true;
    }

    return {
        sport:        sports.join('|'),
        players:      players.join('|'),
        manufacturer: manufacturers.join('|'),
        parallel:     parallels.join('|'),
        set:          sets.join('|'),
        team:         teams.join('|'),
        league:       leagues.join('|'),
        autograph:    hasAutograph ? 'Yes' : 'No',
        cardName:     cardNames.join('|'),
        cardNumber:   cardNumbers.join('|'),
        printRun:     printRuns.join('|'),
    };
}
